import { loadCaseLabels } from 'src/components/pages/Editor/utils/loadTittles'
import type { AnchorLocation } from 'src/components/scene/TextSprite'
import { loadPointOnDomain } from 'src/components/scene/utils'
import { defaultLoadRenderConfig } from 'src/state/stores/loadRenderConfig'
import { formatCompactTwoDecimals } from 'src/utils/numberFormatters'

// labels: shared with the 2D point loads (`FloorplanPointLoadsMesh`)
export const TORUS_TUBE_RADIUS = 0.01
export const LABEL_SCALE = 0.125
/** Label border width in label canvas pixels (the label font is 100 px). */
export const LABEL_BORDER_WIDTH = 8
export const LABEL_DELTA_Y = 0.15
/** Distance from a load outline (or point-load circle) to its label (2 cm beyond the tube). */
export const LABEL_GAP = TORUS_TUBE_RADIUS + 0.02

// shape geometry
/** Smallest width (m) of a non-zero load end, so its arrow heads still fit. */
export const MIN_LOAD_WIDTH = 0.06
/** Distance between the element axis and a load shape (its arrow tips); offsets the whole shape. */
export const LOAD_ELEMENT_GAP = 0.05
/** Length of each arrow head barb. */
export const ARROW_HEAD_SIZE = 0.05
/** Barb angle off the arrow line, as the PDF callout arrows (`CALLOUT_ARROW_HEAD_HALF_ANGLE_DEGREES`). */
export const ARROW_HEAD_HALF_ANGLE_DEGREES = 30
const ARROW_HEAD_SIN = Math.sin((ARROW_HEAD_HALF_ANGLE_DEGREES * Math.PI) / 180)
const ARROW_HEAD_COS = Math.cos((ARROW_HEAD_HALF_ANGLE_DEGREES * Math.PI) / 180)

/** Line widths (px) of the parts of a line load shape. */
export const OUTER_LINE_WIDTH = 2
export const ARROW_LINE_WIDTH = 2

// tolerances
/** Digits of the XY coordinates when grouping loads by extent. */
const ROUNDING_DIGITS = 2
const LENGTH_TOLERANCE = 1e-6

export interface XYZ {
  x: number
  y: number
  z: number
}

export interface FloorplanLineLoadLabel {
  key: string
  load: LineLoadWithDomain
  text: string
  position: XYZ
  anchor: AnchorLocation
  /** Counter-clockwise rotation in radians along the element (see `groupLabels`). */
  rotation: number
}

export interface FloorplanLineLoadGroup {
  key: string
  /** Axis points in a canonical order, independent of the loads' start/end order. */
  axisStart: XYZ
  axisEnd: XYZ
  /** Loads sorted by magnitude in descending order; the first one defines the colour. */
  loads: LineLoadWithDomain[]
  /** Load value drawn at `maxWidth`; all widths scale proportionally to it. */
  maxMagnitude: number
  /** Width (m) of `maxMagnitude`. */
  maxWidth: number
}

export interface FloorplanLineLoadScale {
  /** Width (m) of the largest load value, by default `defaultLoadRenderConfig`'s. */
  maxWidth?: number
  /** Largest load value, by default the largest of the given loads. */
  maxMagnitude?: number
}

const rounded = (value: number) => Number(value.toFixed(ROUNDING_DIGITS))

const pointKey = (point: XYZ) => `${rounded(point.x)},${rounded(point.y)}`

const isBefore = (a: XYZ, b: XYZ) => {
  if (rounded(a.x) !== rounded(b.x)) return rounded(a.x) < rounded(b.x)
  return rounded(a.y) <= rounded(b.y)
}

const toXYZ = ({ x, y, z }: XYZ): XYZ => ({ x, y, z })

const loadEndpoints = (load: LineLoadWithDomain): [XYZ, XYZ] => [
  toXYZ(loadPointOnDomain(load.relative_start, load.domain.start, load.domain.end)),
  toXYZ(loadPointOnDomain(load.relative_end, load.domain.start, load.domain.end)),
]

export const lineLoadMagnitude = (load: LineLoad) =>
  Math.max(Math.abs(load.start.z), Math.abs(load.end.z))

/** Largest start/end value of the given line loads (0 without loads). */
export const maxLineLoadMagnitude = (loads: LineLoad[]) =>
  loads.reduce((max, load) => Math.max(max, lineLoadMagnitude(load)), 0)

/**
 * Groups line loads by their rounded start/end XY (a reversed start/end lands in the same group),
 * like the point loads are grouped by position. Each group is drawn as one geometry. Widths scale
 * so that `maxMagnitude` is drawn at `maxWidth`.
 */
export const groupFloorplanLineLoads = (
  loads: LineLoadWithDomain[],
  {
    maxWidth = defaultLoadRenderConfig.lineLoads.floorplanMaxWidth,
    maxMagnitude = maxLineLoadMagnitude(loads),
  }: FloorplanLineLoadScale = {},
): FloorplanLineLoadGroup[] => {
  const groups = new Map<string, FloorplanLineLoadGroup>()

  loads.forEach(load => {
    if (!load.domain) return

    const [start, end] = loadEndpoints(load)
    const [axisStart, axisEnd] = isBefore(start, end) ? [start, end] : [end, start]
    const key = `${pointKey(axisStart)}|${pointKey(axisEnd)}`

    const group = groups.get(key)
    if (group) group.loads.push(load)
    else groups.set(key, { key, axisStart, axisEnd, loads: [load], maxMagnitude, maxWidth })
  })

  const result = Array.from(groups.values())
  result.forEach(group => group.loads.sort((a, b) => lineLoadMagnitude(b) - lineLoadMagnitude(a)))
  return result
}

/** Mostly horizontal axes get lines above/below and labels below; others lines left/right. */
export const isMostlyHorizontal = (start: XYZ, end: XYZ) =>
  Math.abs(end.x - start.x) >= Math.abs(end.y - start.y)

/** Unit normal of the axis in the XY plane, pointing to -y (or +x for mostly vertical axes). */
export const sideNormal = (start: XYZ, end: XYZ): XYZ => {
  const dx = end.x - start.x
  const dy = end.y - start.y
  const length = Math.hypot(dx, dy)
  if (length === 0) return { x: 0, y: -1, z: 0 }

  const nx = -dy / length
  const ny = dx / length
  const flip = isMostlyHorizontal(start, end) ? ny > 0 : nx < 0
  return { x: (flip ? -nx : nx) || 0, y: (flip ? -ny : ny) || 0, z: 0 }
}

const offsetPoint = (point: XYZ, normal: XYZ, distance: number): XYZ => ({
  x: point.x + normal.x * distance,
  y: point.y + normal.y * distance,
  z: point.z,
})

/** Whether a load value prints as zero in its label. */
const isZeroLoadValue = (value: number) =>
  formatCompactTwoDecimals(Math.abs(value)) === formatCompactTwoDecimals(0)

/**
 * Offsets of a load's outline from the axis at `axisStart` / `axisEnd`, along `sideNormal`.
 * The floorplan y axis stands for the global z axis: negative values (acting downwards) are drawn
 * above the element (left of vertical ones), positive values below (right). Each end's width is
 * proportional to its value: `maxMagnitude` maps to `maxWidth`, but a non-zero end is at least
 * `MIN_LOAD_WIDTH` wide (an end that prints as zero closes a triangle).
 */
export const loadOffsets = (
  load: LineLoadWithDomain,
  { maxMagnitude, maxWidth }: Pick<FloorplanLineLoadGroup, 'maxMagnitude' | 'maxWidth'>,
): [number, number] => {
  const [start, end] = loadEndpoints(load)
  const startsAtAxisStart = isBefore(start, end)
  const offset = (value: number) => {
    if (!(maxMagnitude > 0 && maxWidth > 0) || isZeroLoadValue(value)) return 0
    const width = Math.max(MIN_LOAD_WIDTH, (maxWidth * Math.abs(value)) / maxMagnitude)
    return Math.sign(value) * width
  }

  const startOffset = offset(load.start.z)
  const endOffset = offset(load.end.z)
  return startsAtAxisStart ? [startOffset, endOffset] : [endOffset, startOffset]
}

export interface FloorplanLineLoadShape {
  key: string
  offsets: [number, number]
  /** Largest load with this shape; defines the colour and the context-menu target. */
  load: LineLoadWithDomain
}

/**
 * Distinct shapes of a group (rectangle, trapezoid, triangle, on either side), one per shape so
 * e.g. a constant and a varying load on the same extent are both visible. Loads are already sorted.
 */
export const groupShapes = (group: FloorplanLineLoadGroup): FloorplanLineLoadShape[] => {
  const shapes = new Map<string, FloorplanLineLoadShape>()
  group.loads.forEach(load => {
    const offsets = loadOffsets(load, group)
    const key = offsets.map(offset => offset.toFixed(6)).join('|')
    if (!shapes.has(key)) shapes.set(key, { key, offsets, load })
  })
  return Array.from(shapes.values())
}

/** Parameter range in [0, 1] where the linear `start` → `end` is at least `limit`. */
const rangeAtLeast = (start: number, end: number, limit: number): [number, number] | undefined => {
  if (start === end) return start >= limit ? [0, 1] : undefined
  const t = (limit - start) / (end - start)
  const range: [number, number] = end > start ? [Math.max(0, t), 1] : [0, Math.min(1, t)]
  return range[0] < range[1] ? range : undefined
}

/**
 * Number of equal intervals between the arrows (including the start and end arrows): one per full
 * meter, but at least two so there is always an arrow in the middle. E.g. 2.2 m → 2 intervals of
 * 1.1 m.
 */
export const arrowIntervalCount = (length: number) =>
  Math.max(2, Math.floor(length + LENGTH_TOLERANCE))

export interface FloorplanLineLoadShapeSegments {
  /** Line along the load magnitude, away from the element. */
  outer: [XYZ, XYZ][]
  /** Orthogonal lines at the start and end, and the arrows (with their barbs) in between. */
  arrows: [XYZ, XYZ][]
}

/**
 * Line segments of one shape: the outer line, an orthogonal line at the start and the end and
 * evenly spaced arrows (with two barbs) in between (`arrowIntervalCount`). The whole shape is
 * moved `LOAD_ELEMENT_GAP` away from the axis; the barbs stay inside the load zone.
 */
export const shapeSegments = (
  { axisStart, axisEnd }: FloorplanLineLoadGroup,
  [startOffset, endOffset]: [number, number],
): FloorplanLineLoadShapeSegments => {
  const outer: [XYZ, XYZ][] = []
  const arrows: [XYZ, XYZ][] = []

  const length = Math.hypot(axisEnd.x - axisStart.x, axisEnd.y - axisStart.y)
  if (length === 0) return { outer, arrows }

  const normal = sideNormal(axisStart, axisEnd)
  const direction: XYZ = {
    x: (axisEnd.x - axisStart.x) / length,
    y: (axisEnd.y - axisStart.y) / length,
    z: 0,
  }
  const offsetAt = (t: number) => startOffset + (endOffset - startOffset) * t
  const pointAt = (t: number, offset: number) =>
    offsetPoint(offsetPoint(axisStart, direction, length * t), normal, offset)

  if (startOffset === 0 && endOffset === 0) return { outer, arrows }

  // parts of the range on the positive / negative side; the whole shape is moved away from the
  // element by LOAD_ELEMENT_GAP on its side
  const sides = [
    { side: 1, range: rangeAtLeast(startOffset, endOffset, 0) },
    { side: -1, range: rangeAtLeast(-startOffset, -endOffset, 0) },
  ]
  sides.forEach(({ side, range }) => {
    if (!range) return
    const [from, to] = range
    const outerAt = (t: number) => pointAt(t, offsetAt(t) + side * LOAD_ELEMENT_GAP)
    outer.push([outerAt(from), outerAt(to)])
  })

  /** Orthogonal line at `t` from the gap to the outer line; its tip, unless too shallow. */
  const pushOrthogonalLine = (t: number) => {
    const offset = offsetAt(t)
    if (Math.abs(offset) <= LENGTH_TOLERANCE) return undefined

    const side = Math.sign(offset)
    const tip = pointAt(t, side * LOAD_ELEMENT_GAP)
    arrows.push([tip, pointAt(t, offset + side * LOAD_ELEMENT_GAP)])
    return tip
  }

  /** Orthogonal line at `t` with an arrow head towards the element, skipped if too shallow. */
  const pushArrow = (t: number, narrowing: number, maxHeadSize: number) => {
    const tip = pushOrthogonalLine(t)
    if (!tip) return

    const offset = offsetAt(t)
    const depth = Math.abs(offset)
    const side = Math.sign(offset)

    // the zone may narrow next to the arrow, so shrink the head until it fits
    const wingLength = Math.min(
      ARROW_HEAD_SIZE,
      depth / (ARROW_HEAD_COS + narrowing * ARROW_HEAD_SIN),
      maxHeadSize / ARROW_HEAD_SIN,
    )
    const barbEnd = (along: number) =>
      offsetPoint(
        offsetPoint(tip, direction, along * wingLength * ARROW_HEAD_SIN),
        normal,
        side * wingLength * ARROW_HEAD_COS,
      )
    arrows.push([tip, barbEnd(1)], [tip, barbEnd(-1)])
  }

  pushOrthogonalLine(0)
  pushOrthogonalLine(1)

  const slope = Math.abs(endOffset - startOffset) / length
  const intervals = arrowIntervalCount(length)
  for (let index = 1; index < intervals; index += 1) {
    pushArrow(index / intervals, slope, length / intervals / 2)
  }

  return { outer, arrows }
}

const loadCaseTypeShort = (load: LineLoad) =>
  loadCaseLabels[load.load_case.category][load.load_case.load_case_type as LoadCaseTypes]?.short ||
  '-'

export const lineLoadLabelText = (value: number, load: LineLoad) =>
  `${formatCompactTwoDecimals(value * -1)}N/m ${loadCaseTypeShort(load)}`

export const isConstantLineLoad = (load: LineLoad) =>
  formatCompactTwoDecimals(load.start.z) === formatCompactTwoDecimals(load.end.z)

type LabelSlot = 'center' | 'axisStart' | 'axisEnd'

/** Side of a load value: 1 below (right of) the element, -1 above (left of) it. */
const valueSide = (value: number, fallback: number) => {
  if (isZeroLoadValue(value)) return fallback
  return value < 0 ? -1 : 1
}

/**
 * Labels of a group, one per constant load (centred) and two per varying load (at its start and
 * end, aligned so they end inside the load range). Each label sits just beyond the outline on the
 * side its value is drawn; labels at the same slot and side stack away from the element.
 *
 * Labels are rotated along the element, read so that `sideNormal` points down (as the load
 * sides): mostly horizontal elements read left to right, mostly vertical ones with the head tilted
 * left, i.e. rotations between -45° and 135°.
 */
export const groupLabels = (
  group: FloorplanLineLoadGroup,
  shapes: FloorplanLineLoadShape[] = groupShapes(group),
): FloorplanLineLoadLabel[] => {
  const { axisStart, axisEnd } = group
  const normal = sideNormal(axisStart, axisEnd)
  // reading direction: the side normal turned 90° counter-clockwise
  const reading: XYZ = { x: -normal.y, y: normal.x, z: 0 }
  const rotation = Math.atan2(reading.y, reading.x)
  const middle: XYZ = {
    x: (axisStart.x + axisEnd.x) / 2,
    y: (axisStart.y + axisEnd.y) / 2,
    z: (axisStart.z + axisEnd.z) / 2,
  }
  const pointOf: Record<LabelSlot, XYZ> = { center: middle, axisStart, axisEnd }
  const alongReading = (axisEnd.x - axisStart.x) * reading.x + (axisEnd.y - axisStart.y) * reading.y
  const readingStartSlot: LabelSlot = alongReading >= 0 ? 'axisStart' : 'axisEnd'

  // distance from the axis to the outermost outline on each side
  const offsets = shapes.flatMap(shape => shape.offsets)
  const extentOn = (side: number) =>
    LOAD_ELEMENT_GAP + Math.max(0, ...offsets.map(offset => offset * side))

  const anchorOf = (slot: LabelSlot, side: number): AnchorLocation => {
    // above the element the label hangs on its bottom edge, below it on its top edge
    const edge = side < 0 ? 'bottom' : 'top'
    if (slot === 'center') return edge
    return `${edge}-${slot === readingStartSlot ? 'left' : 'right'}`
  }

  const counts = new Map<string, number>()
  const labels: FloorplanLineLoadLabel[] = []

  const push = (
    slot: LabelSlot,
    side: number,
    load: LineLoadWithDomain,
    value: number,
    suffix: string,
  ) => {
    const countKey = `${slot}|${side}`
    const index = counts.get(countKey) ?? 0
    counts.set(countKey, index + 1)
    const distance = extentOn(side) + LABEL_GAP + index * LABEL_DELTA_Y
    labels.push({
      key: `${load.guid}-${suffix}`,
      load,
      text: lineLoadLabelText(value, load),
      position: offsetPoint(pointOf[slot], normal, side * distance),
      anchor: anchorOf(slot, side),
      rotation,
    })
  }

  group.loads.forEach(load => {
    if (isConstantLineLoad(load)) {
      push('center', valueSide(load.start.z, 1), load, load.start.z, 'center')
      return
    }

    // a zero end belongs to the side of the other end
    const startSide = valueSide(load.start.z, valueSide(load.end.z, 1))
    const endSide = valueSide(load.end.z, startSide)
    const [start, end] = loadEndpoints(load)
    const startsAtAxisStart = isBefore(start, end)
    push(startsAtAxisStart ? 'axisStart' : 'axisEnd', startSide, load, load.start.z, 'start')
    push(startsAtAxisStart ? 'axisEnd' : 'axisStart', endSide, load, load.end.z, 'end')
  })

  return labels
}
